// Recipient order history — a read-only feed of orders sent to them, from
// their own perspective, with the delivery receipt downloadable once confirmed.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, History as HistoryIcon, MapPin, RotateCcw } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState, StatusPill } from '../../components/shared/primitives';
import { RECIPIENT_NAV } from '../_navs';
import { requests, type RequestStatus, type ServiceRequestWithItems } from '../../lib/api';
import { ReceiptLink } from '../customer/HistoryPage';

const TERMINAL: RequestStatus[] = ['confirmed', 'cancelled', 'refunded', 'disputed'];

export function RecipientHistoryPage() {
  const q = useQuery({ queryKey: ['recipient', 'requests'], queryFn: () => requests.list() });

  const rows = useMemo(() =>
    (q.data ?? [])
      .filter((r) => TERMINAL.includes(r.status))
      .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
    [q.data]);

  return (
    <PortalShell nav={RECIPIENT_NAV} activeId="history" title="History" subtitle="Orders sent to you, with delivery receipts.">
      <Card padding="p-0">
        {q.isLoading ? (
          <div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={HistoryIcon} title="Nothing here yet" body="Once a delivery is confirmed, it shows up here with its receipt." />
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => <RecipientHistoryItem key={r.id} r={r} />)}
          </div>
        )}
      </Card>
    </PortalShell>
  );
}

function fmtDate(s?: string) {
  return s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

function RecipientHistoryItem({ r }: { r: ServiceRequestWithItems }) {
  return (
    <div className="px-4 py-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-[14px] font-semibold text-slate-900 truncate">{r.title || r.category?.label || `Cat ${r.category_id}`}</p>
      <div className="mt-1 flex items-center gap-2 text-[12px] text-slate-500">
        {r.city?.name && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /> {r.city.name}</span>}
        <span>· {fmtDate(r.updated_at)}</span>
        {r.cart_item_count > 0 && <span>· {r.cart_item_count} items</span>}
      </div>
      {r.status === 'confirmed' && (
        <div className="mt-2 flex items-center gap-3">
          <Link to={`/recipient/request/new?reorder=${r.id}`} className="text-[11.5px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Order again</Link>
          <ReceiptLink requestId={r.id} />
        </div>
      )}
    </div>
  );
}
